import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { Container, Reveal, SectionHeader } from '../components/Reveal';
import Journey from '../components/Journey';
import ArticleCard from '../components/ArticleCard';
import FAQ from '../components/FAQ';
import { WhatsAppButton, BookButton } from '../components/CTAs';
import { breadcrumbSchema, faqSchema, serviceSchema } from '../lib/schema';
import { fetchArticles } from '../lib/api';
import { track } from '../lib/track';
import { TREATMENTS, getTreatment } from '../data/treatments';
import { DOCTORS } from '../data/doctors';

function Bullets({ items, className = '' }) {
    return (
        <ul className={`space-y-3 ${className}`}>
            {items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[15px] leading-relaxed text-ink-body">
                    <span className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-gold-600" />
                    {it}
                </li>
            ))}
        </ul>
    );
}

export default function TreatmentPage() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const treatment = getTreatment(slug);
    const [related, setRelated] = useState([]);
    useEffect(() => {
        if (treatment) {
            track('treatment_view', { treatment: slug });
            fetchArticles().then((all) => setRelated(all.filter((a) => a.related_treatment === slug).slice(0, 3))).catch(() => {});
        }
    }, [slug, treatment]);

    if (!treatment) return <NotFound />;
    const c = { ...(treatment.en || treatment.ms), ...(treatment[lang] || {}) };

    return (
        <>
            <SEO
                title={`${c.metaTitle}`}
                description={c.metaDesc}
                path={`/treatments/${treatment.slug}`}
                lang={lang}
                jsonLd={[
                    faqSchema(c.faqs),
                    serviceSchema(treatment.slug, c.name, c.short, lang),
                    breadcrumbSchema(lang, [
                        { name: t.nav.home, path: '/' },
                        { name: c.name, path: `/treatments/${treatment.slug}` },
                    ]),
                ]}
            />
            {/* Hero */}
            <section className="relative overflow-hidden pt-28 md:pt-36">
                <div className="pointer-events-none absolute -right-32 -top-20 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
                        <Reveal>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{c.kicker}</p>
                            <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-5xl lg:text-6xl">{c.heroTitle}</h1>
                            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{c.heroSub}</p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <WhatsAppButton label={t.cta.whatsappLong} source={`treatment-${slug}`} />
                                <BookButton label={t.cta.book} lang={lang} source={`treatment-${slug}`} />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <img src={treatment.img} alt={c.name} className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="treatment-hero-img" />
                        </Reveal>
                    </div>
                </Container>
            </section>

            {/* Who + Problem */}
            <section className="py-16 md:py-24">
                <Container>
                    <div className="grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.who}</h2>
                            <p className="mt-3 text-sm text-ink-subtle">{c.whoIntro}</p>
                            <Bullets items={c.who} className="mt-6" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.problem}</h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-ink-body">{c.problem}</p>
                        </Reveal>
                    </div>
                </Container>
            </section>

            {/* Assessment + Options */}
            <section className="bg-peri-50 py-16 md:py-24">
                <Container>
                    <div className="grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.assessment}</h2>
                            <Bullets items={c.assessment} className="mt-6" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.options}</h2>
                            <p className="mt-3 text-sm text-ink-subtle">{c.optionsIntro}</p>
                            <div className="mt-6 space-y-4">
                                {c.options.map((o) => (
                                    <div key={o.name} className="rounded-2xl border border-peri-200 bg-white p-6">
                                        <p className="font-semibold text-gold-700">{o.name}</p>
                                        <p className="mt-2 text-sm leading-relaxed text-ink-muted">{o.text}</p>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </section>

            {/* Journey */}
            <section className="py-16 md:py-24">
                <Container>
                    <SectionHeader eyebrow={t.home.journey.eyebrow} title={t.treatLabels.journey} />
                    <Journey />
                </Container>
            </section>

            {/* Know + followup */}
            <section className="bg-peri-50 py-16 md:py-24">
                <Container>
                    <div className="grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.know}</h2>
                            <p className="mt-3 text-sm text-ink-subtle">{c.knowIntro}</p>
                            <Bullets items={c.know} className="mt-6" />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.followup}</h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-ink-body">{c.followup}</p>
                            {(c.teenNote || c.intlNote) && (
                                <div className="mt-8 space-y-4">
                                    {c.teenNote && (
                                        <div className="rounded-2xl border border-peri-200 bg-white p-6" data-testid="treatment-teen-note">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.treatLabels.teenNoteLabel}</p>
                                            <p className="mt-2 text-sm leading-relaxed text-ink-muted">{c.teenNote}</p>
                                            <Link to={`/dental/${lang}/teen-smile`} className="mt-3 inline-block text-sm font-semibold text-gold-700 underline underline-offset-4">{t.nav.teen}</Link>
                                        </div>
                                    )}
                                    {c.intlNote && (
                                        <div className="rounded-2xl border border-peri-200 bg-white p-6" data-testid="treatment-intl-note">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.treatLabels.intlNoteLabel}</p>
                                            <p className="mt-2 text-sm leading-relaxed text-ink-muted">{c.intlNote}</p>
                                            <Link to={`/dental/${lang}/international`} className="mt-3 inline-block text-sm font-semibold text-gold-700 underline underline-offset-4">{t.nav.international}</Link>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Reveal>
                    </div>
                </Container>
            </section>

            {/* Extra sections (e.g. Artist Smile) */}
            {c.extraSections && c.extraSections.length > 0 && (
                <section className="py-16 md:py-24" data-testid="treatment-extra">
                    <Container>
                        <div className="grid gap-4 md:grid-cols-3">
                            {c.extraSections.map((s, i) => (
                                <Reveal key={s.h} delay={i * 0.07} className="h-full">
                                    <div className="h-full rounded-3xl border border-peri-200 bg-white p-8">
                                        <h2 className="font-serif text-xl font-semibold text-ink">{s.h}</h2>
                                        <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">{s.p}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            {c.digitalNote && (
                <section className="pb-16 md:pb-24" data-testid="treatment-digital-note">
                    <Container>
                        <Reveal>
                            <div className="rounded-3xl border border-peri-200 bg-peri-50 p-7 md:p-8">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.treatLabels.digital}</p>
                                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-muted">{c.digitalNote}</p>
                                <Link to={`/dental/${lang}/digital-dentistry`} className="mt-3 inline-block text-sm font-semibold text-gold-700 underline underline-offset-4">{t.nav.digitalLink.title}</Link>
                            </div>
                        </Reveal>
                    </Container>
                </section>
            )}

            {/* FAQ */}
            <section className="py-16 md:py-24">
                <Container>
                    <SectionHeader eyebrow={t.treatLabels.faq} title={t.treatLabels.faq} />
                    <Reveal><FAQ faqs={c.faqs} testPrefix="treatment-faq" /></Reveal>
                </Container>
            </section>

            {/* Doctors */}
            <section className="bg-peri-50 py-16 md:py-24">
                <Container>
                    <SectionHeader eyebrow={t.home.doctors.eyebrow} title={t.treatLabels.doctors} />
                    <div className="grid gap-5 md:grid-cols-2">
                        {DOCTORS.map((d, i) => (
                            <Reveal key={d.slug} delay={i * 0.08}>
                                <Link to={`/dental/${lang}/doctors/${d.slug}`} data-testid={`treatment-doctor-${d.slug}`} className="block rounded-3xl border border-peri-200 bg-white p-7 transition-all hover:border-gold-500 hover:shadow-card-hover">
                                    <h3 className="font-serif text-2xl font-semibold text-ink">{d.name}</h3>
                                    <p className="mt-1 text-sm font-semibold text-gold-700">{d.creds}</p>
                                    <p className="mt-3 text-sm text-ink-muted">{d.key === 'irma' ? t.home.doctors.irmaLine : t.home.doctors.ariandiLine}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Related articles */}
            {related.length > 0 && (
                <section className="py-16 md:py-24">
                    <Container>
                        <SectionHeader eyebrow={t.kolum.title} title={t.treatLabels.articles} />
                        <div className="grid gap-5 md:grid-cols-3">
                            {related.map((a, i) => (
                                <Reveal key={a.slug} delay={i * 0.07} className="h-full"><ArticleCard article={a} /></Reveal>
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            {/* Related treatments — semantic internal linking */}
            <section className="border-t border-peri-200 py-12" data-testid="related-treatments">
                <Container>
                    <h2 className="font-serif text-xl font-semibold text-ink">{t.nav.treatments}</h2>
                    <div className="mt-5 flex flex-wrap gap-3">
                        {TREATMENTS.filter((x) => x.slug !== treatment.slug).map((x) => {
                            const xc = x[lang] || x.en;
                            return (
                                <Link key={x.slug} to={`/dental/${lang}/treatments/${x.slug}`} data-testid={`related-treatment-${x.slug}`} className="rounded-full border border-peri-300 px-4 py-2 text-sm text-ink-muted transition-colors hover:border-gold-500 hover:text-gold-700">
                                    {xc.name}
                                </Link>
                            );
                        })}
                        <Link to={`/dental/${lang}/digital-dentistry`} data-testid="related-treatment-digital" className="rounded-full border border-peri-300 px-4 py-2 text-sm text-ink-muted transition-colors hover:border-gold-500 hover:text-gold-700">
                            {t.nav.digitalLink.title}
                        </Link>
                    </div>
                </Container>
            </section>

            {/* CTA */}
            <section className="bg-ink py-16 text-white md:py-24">
                <Container>
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <h2 className="font-serif text-3xl font-semibold md:text-4xl">{t.treatLabels.ctaTitle}</h2>
                        <p className="mt-4 text-base leading-relaxed text-peri-200">{t.treatLabels.ctaText}</p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <WhatsAppButton label={t.cta.whatsappLong} source={`treatment-${slug}-bottom`} />
                            <BookButton label={t.cta.book} lang={lang} source={`treatment-${slug}-bottom`} className="!border-peri-300 !text-peri-200 hover:!bg-white/10" />
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
